import { debounce } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Returns a ref and px value of the distance from the top of the viewport to the top of the element.
 * Automatically updates when the window is resized (debounced).
 *
 * @param anchorRef - When provided, this ref's offsetTop is used as the measurement anchor
 *   instead of the element's own offsetTop — useful when elements between the anchor and the
 *   target (and any gaps) should be excluded.
 */
function useOffsetTop(delay = 250, anchorRef?: React.RefObject<HTMLElement>) {
  const [offsetTop, setOffsetTop] = useState(0);
  const ref = useRef<HTMLElement | null>(null);
  const anchorRefRef = useRef(anchorRef);
  anchorRefRef.current = anchorRef;

  const onChange = useCallback((el: HTMLElement) => {
    if (!el) return;
    const target = anchorRefRef.current?.current ?? el;
    // Measured against the viewport rather than the offsetParent, so a
    // positioned ancestor can't silently shrink the value.
    const next = target.getBoundingClientRect().top + window.scrollY;
    setOffsetTop(previous => (Math.abs(previous - next) > 1 ? next : previous));
  }, []);

  useEffect(() => {
    const onRefChange = () => {
      if (ref.current) onChange(ref.current);
    };
    const handleResize = debounce(onRefChange, delay);
    window.addEventListener('resize', handleResize);
    onRefChange();

    // Banners mount after first render and push the element down, which a
    // resize listener alone never sees.
    const observer = new ResizeObserver(onRefChange);
    if (ref.current?.parentElement) observer.observe(ref.current.parentElement);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.clear();
      observer.disconnect();
    };
  }, [delay, onChange]);

  return [ref, offsetTop, onChange] as const;
}

export default useOffsetTop;
